/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2024 Prodeo - All Rights Reserved
 */
/* ===== External Libraries ===== */
import { APIError, AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, dayjs, FORMAT_DATE_API, SafetyCrossDay, SafetyCrossStatistics, Sector, UUID } from '@umanforge/common';
import { AxiosResponse, AxiosError } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

/* ===== API Configuration ===== */
import { API } from 'constants/api.constant';

/* ===== Services ===== */
import { GlobalService } from './global.service';

/* ===== Interfaces ===== */
import { AdminTableWithSitesApiResponse, EventManager } from 'interfaces/eventManager.interface';
import { SectorSetting } from 'interfaces/sectorSetting.interface';
import { EventCommitment } from 'interfaces/types.interface';
import { Theme } from 'interfaces/theme.interface';
import { User } from 'interfaces/user.interface';
import { FieldSettingResponse } from 'interfaces/fieldSetting.interface';
import { EventData } from 'interfaces/eventData.interface';
import { EventDocument } from 'interfaces/document.interface';
import { UsersService } from './users.service';
import { Note } from 'interfaces/note.interface';
import { FORMAT_MONTH_API } from '@umanforge/common/dist/constants/date.constants';
import { ChartWithDataApi } from 'interfaces/chartWithData.interface';


export class VisualisationService {

  /* ===== Active visualisation session for an event manager ===== */
  static active(
    eventManagersId: UUID[] | undefined, sectorId: UUID | undefined,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.post( `${API.VISUALISATION}/activate`, { eventManagersId, sectorId }, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ token: string } & APIError> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          if ( response.data.token ) {
            CommonGlobalService.saveRequestVisualisationToken( response.data.token, dispatch );
          }
          resolve( response.status === 200 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Reactive visualisation session for an event manager ===== */
  static renew(
    visualisationToken: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ token: string } & APIError> {
    return new Promise( resolve => {
      AXIOS.post( `${API.VISUALISATION}/renew`, {}, CommonGlobalService.getVisualisationHeaderRequest( visualisationToken ) )
        .then( ( response: AxiosResponse<{ token: string } & APIError> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          if ( response.data.token ) {
            CommonGlobalService.saveRequestVisualisationToken( response.data.token, dispatch );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get the sector of visualisation session ===== */
  static getSector(
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Sector | null> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/sector`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Sector | null> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get all chart info for visualisation session of an event manager ===== */
  static getChartsDatasOfEventManager(
    eventManagerId: UUID,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<ChartWithDataApi[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/chart_data/${ eventManagerId }`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<ChartWithDataApi[]> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get all settings of a sector of visualisation sessions ===== */
  static getSectorSettings(
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<SectorSetting[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/sector_setting`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<SectorSetting[]> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get all settings of a sector of visualisation sessions ===== */
  static getSectorSetting(
    key: string,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<SectorSetting> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/sector_setting/${ key }`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<SectorSetting> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get infos to event manager of visualisation sessions ===== */
  static getEventManager(
    eventManagerId: string,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventManager> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/event_manager/${eventManagerId}`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EventManager> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get infos to all event managers of visualisation sessions ===== */
  static getEventManagers(
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventManager[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/event_managers`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EventManager[]> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get all engagements of the visualisation session on a range period ===== */
  static getEngagements(
    eventManagerId: string, startDate: dayjs.Dayjs, endDate: dayjs.Dayjs,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventCommitment[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/engagements/${eventManagerId}/${startDate.format( FORMAT_DATE_API )}/${endDate.format( FORMAT_DATE_API )}`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EventCommitment[]> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get all missing days of the visualisation session on a range period ===== */
  static getMissingDayOfEventManagerByfUser(
    eventManagerId: string, startDate: dayjs.Dayjs, endDate: dayjs.Dayjs,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ [userId: UUID]: { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs; }[] }> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/close_days/${eventManagerId}/${startDate.format( FORMAT_DATE_API )}/${endDate.format( FORMAT_DATE_API )}`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ [userId: UUID]: { startDate: string; endDate: string; }[] }> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const keys = Object.keys( response.data );
          const missingDays: { [userId: UUID]: { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs; }[] } = {};
          for ( const key of keys ) {
            missingDays[ key ] = response.data[ key ].map( range => {
              return { startDate: dayjs( range.startDate, FORMAT_DATE_API ), endDate: dayjs( range.endDate, FORMAT_DATE_API ) };
            } );
          }
          resolve( missingDays );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* =====  ===== */
  static getStatisticsOfSafetyCross(
    eventManagerId: string,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<SafetyCrossStatistics> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/safety_cross/${ eventManagerId }/statistics`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<SafetyCrossStatistics> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
            resolve( {
              bestConsecutiveDays: 0,
              currentConsecutiveDays: 0,
              totalAccidents: 0,
            } );
          } else {
            resolve( response.data );
          }
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( {
            bestConsecutiveDays: 0,
            currentConsecutiveDays: 0,
            totalAccidents: 0,
          } );
        } );
    } );
  }

  /* =====  ===== */
  static getSafetyCrossOfEventManagerByMonth(
    eventManagerId: string, month: dayjs.Dayjs,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<SafetyCrossDay[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/safety_cross/${ eventManagerId }/${ month.format( FORMAT_MONTH_API ) }`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<SafetyCrossDay[]> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
            resolve( [] );
          } else {
            resolve( response.data );
          }
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
          resolve( [] );
        } );
    } );
  }

  /* ===== Get all event managers where the user is participant or admin ===== */
  static getEventManagersWhereParticipantOrAdmin(
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<AdminTableWithSitesApiResponse[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/event_manager/participant-or-admin`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<AdminTableWithSitesApiResponse[]> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }


  /* ===== Get common themes of event managers ===== */
  static getCommonThemesByEventManager(
    eventManagerIds: UUID[],
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Theme[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/themes/common-by-event-managers`, { ...CommonGlobalService.getVisualisationHeaderRequest( token ), params: { eventManagerIds: eventManagerIds } } )
        .then( ( response: AxiosResponse<Theme[]> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get common users of event managers ===== */
  static getCommonUsersByEventManager(
    eventManagerIds: UUID[],
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<User[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/users/common-by-event-managers`, { ...CommonGlobalService.getVisualisationHeaderRequest( token ), params: { eventManagerIds: eventManagerIds } } )
        .then( ( response: AxiosResponse<User[]> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get fields setting of site ===== */
  static getFieldsSettingOfSite(
    siteId: UUID,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<FieldSettingResponse | undefined> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/field-settings/${ siteId }`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<FieldSettingResponse | undefined> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get an event by ID ===== */
  static getEventById(
    eventId: UUID,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventData> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/event/${ eventId }`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EventData> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get fields of event ===== */
  static getFieldValuesByEventId(
    eventId: UUID,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<unknown[] | undefined> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/fields/${ eventId }`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<unknown[] | undefined> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get avatars of users ===== */
  static getUsersAvatars(
    usersId: UUID[], clientId: UUID,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<{ userId: string, avatar: string | null }[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/users/avatars`, { ...CommonGlobalService.getVisualisationHeaderRequest( token ), params: { usersId, clientId } } )
        .then( ( response: AxiosResponse<{ avatars: { userId: string, avatar: string | null }[] }> ) => {
          const result: { userId: string, avatar: string | null }[] = [];
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          if ( response.status === 200 && response.data.avatars ) {
            result.push( ...UsersService.formattedUsersAvatarsResponse( response.data.avatars ) );
          }
          resolve( result );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get avatars of users ===== */
  static getUserAvatar(
    userId: UUID, clientId: UUID,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<string | null> {
    return new Promise( resolve => {
      VisualisationService.getUsersAvatars( [ userId ], clientId, visualisationToken, dispatch, onError, onWarning )
        .then( response => {
          let result: string | null = null;
          if ( response && response.length > 0 ) {
            result = response[ 0 ].avatar;
          }
          resolve( result );
        } );
    } );
  }

  /* ===== Get documents of event ===== */
  static getDocumentsByEventId(
    eventId: UUID,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<EventDocument[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/documents/${ eventId }`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<EventDocument[]> ) => {
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get notes of event ===== */
  static getNotesByEventId(
    eventId: UUID, siteId: UUID,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Note[]> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/notes/${ eventId }/site/${ siteId }`, CommonGlobalService.getVisualisationHeaderRequest( token ) )
        .then( ( response: AxiosResponse<Note[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }


  /* ===== Send request to download a document ===== */
  static downloadDocument(
    documentId: UUID,
    visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<Blob> {
    return new Promise( async resolve => {
      const token = await VisualisationService.getVisualisationTokenUpdated( visualisationToken, dispatch );
      AXIOS.get( `${API.VISUALISATION}/documents/${ documentId }/download`, { ...CommonGlobalService.getVisualisationHeaderRequest( token ), responseType: 'blob' } )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          const blob = new Blob( [ response.data ], { type: response.headers[ 'content-type' ] as string | undefined } );
          resolve( blob );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Get an updated visualisation token ===== */
  static async getVisualisationTokenUpdated( visualisationToken: { token: string, expirationDate: Dayjs } | null, dispatch: Dispatch ): Promise<string | null> {
    let newToken = null;
    if ( visualisationToken ) {
      const now = dayjs();
      if ( now.isAfter( visualisationToken.expirationDate, 'second' ) || now.isSame( visualisationToken.expirationDate, 'second' ) ) {
        const result = await VisualisationService.renew( visualisationToken.token, dispatch );
        if ( result.token ) {
          newToken = result.token;
        }
      } else {
        newToken = visualisationToken.token;
      }
    }
    return newToken;
  }

}
